import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  DefaultValues,
  FieldValues,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { SystemFieldProofreadingStatus } from "../shared/enums/shared-enums";
import { AttributesMetadata } from "../types/attributeMetadata";
import { BaseNodeEntity } from "../types/baseNodeEntity";
import { useFetchUserEnums } from "../hooks/query/useUserEnumQuery";
import { DeleteDialogFunctionality } from "../components/modals/DeleteDialog/DeleteDialog";
import { isCancelledError } from "@tanstack/react-query";
import { BaseEntityWithMetadata } from "../types/baseRecordEntity";
import { getErrorMessage } from "../hooks/query/entityQueryClient";

// ============================================
// INTERFACE DEFINITIONS
// ============================================

/**
 * Unified controller interface for both view and edit pages.
 *
 * This is the single source of truth for entity controllers. It combines
 * the functionality previously split between BaseEntityController,
 * FormDataController, and ViewDataController.
 *
 * Usage:
 * - View pages use: fetchData, title, userEnums, getEditRoute
 * - Edit pages use: all properties (form-specific ones like onSubmit, onDelete, etc.)
 *
 * The unused properties are initialized but never called on view pages,
 * which is an acceptable tradeoff for having a unified interface.
 */
export interface EntityController<T extends FieldValues> {
  // ========== BASE (shared between view and edit) ==========

  /** Fetches the entity data. Returns default values in create mode. */
  fetchData: () => Promise<T>;

  /** Generates the display title from entity data */
  title?: (data: T) => string;

  /** List of user enum types needed for this entity */
  userEnums?: string[];

  // ========== VIEW-SPECIFIC ==========

  /** Returns the route to the edit page for this entity. Optional for dialog-based controllers. */
  getEditRoute?: () => string;

  // ========== FORM-SPECIFIC ==========

  /** Default form values for react-hook-form */
  defaultValues: DefaultValues<T>;

  /** Handler for form submission (create or update) */
  onSubmit: (data: T) => Promise<T | Record<string, string>>;

  /** Handler for entity deletion (optional) */
  onDelete?: () => Promise<void>;

  /** Warning message shown in delete confirmation dialog */
  deleteWarningMessage?: string;

  /** Whether this is a create (true) or edit (false) operation */
  isCreateMode: boolean;

  /** Field names that have metadata attached */
  metadataFieldNames?: string[];

  /** Callback executed after successful form submission or cancellation */
  formCallback?: () => void;
}

/**
 * Connection controller interface - extends EntityController with dialog-specific properties.
 */
export interface ConnectionController<
  T extends FieldValues,
  TSource extends BaseNodeEntity,
  TDestination extends BaseNodeEntity,
> extends EntityController<T> {
  openCreateDialog: () => void;
  openEditDialog: (id: string) => void;
  closeDialog: () => void;
  isDialogShown: boolean;
  direction: "inbound" | "outbound";
  anchorEntity: TSource | TDestination;
  from: TSource | null;
  to: TDestination | null;
}

// ============================================
// HANDLER RETURN TYPE
// ============================================

/**
 * Return type for the useEntityHandler hook.
 * Contains all state and methods needed to manage an entity page (both form and view).
 */
export interface EntityHandlerReturn<T extends FieldValues> {
  title?: string;
  loading: boolean;
  globalError: string | null;
  methods: UseFormReturn<T>;
  isSubmitting: boolean;
  isDeleting: boolean;
  isProcessing: boolean;
  isCreateMode: boolean;
  onSubmit: () => void;
  deleteFunctionality?: DeleteDialogFunctionality;
  onCancel: () => void;
  userEnums: Record<string, string[]>;
  isMetadataFieldVisible: (field: string) => boolean;
  areAllMetadataVisible: boolean;
  toggleAllMetadataVisibility: () => void;
  toggleMetadataFieldVisibility: (field: string) => void;
  setMetadataFieldVisibility: (field: string, value: boolean) => void;
  hasMetadataField: (field: string) => boolean;
  hasMetadata: boolean;
  fetchError: string | null;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Initialize metadata fields with default proofreading status.
 */
export const initMetadata = <T extends BaseEntityWithMetadata>(
  fields: Array<keyof T>
): AttributesMetadata =>
  fields.reduce(
    (acc, field) => ({
      ...acc,
      [field]: {
        items: [],
        system_proofreading_status:
          SystemFieldProofreadingStatus.NO_PROOFREADING,
      },
    }),
    {} as AttributesMetadata
  );

// ============================================
// ENTITY HANDLER HOOK
// ============================================

/**
 * Hook that manages entity state for both view and edit pages.
 * This is the single handler used by all entity pages.
 *
 * @param controller - Entity controller configuration
 * @returns EntityHandlerReturn with all state and methods
 */
export const useEntityHandler = <T extends FieldValues>(
  controller:
    | EntityController<T>
    | ConnectionController<T, BaseNodeEntity, BaseNodeEntity>
): EntityHandlerReturn<T> => {
  const {
    fetchData,
    title: getTitle,
    isCreateMode,
    onSubmit: submitFn,
    metadataFieldNames: metadataFields,
    defaultValues,
    onDelete: deleteFn,
    deleteWarningMessage,
    formCallback = () => {},
  } = controller;

  const methods = useForm<T>({
    defaultValues,
    mode: "onChange",
  });
  const { handleSubmit, reset, formState, getValues } = methods;

  const fetchUserEnums = useFetchUserEnums(controller.userEnums || []);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [userEnums, setUserEnums] = useState<Record<string, string[]>>({});
  const [visibleMetadata, setVisibleMetadata] = useState<{
    [key: string]: boolean;
  }>(
    metadataFields?.reduce((obj, field) => ({ ...obj, [field]: false }), {}) ||
      {}
  );

  const maybeConnection = controller as ConnectionController<
    T,
    BaseNodeEntity,
    BaseNodeEntity
  >;
  const isDialogShown =
    typeof maybeConnection.isDialogShown === "boolean"
      ? maybeConnection.isDialogShown
      : true;

  // Track the dialog state to prevent re-fetching when functions change
  const lastDialogState = useRef<boolean | null>(null);

  const handleFormCallback = () => {
    formCallback();
    reset();
  };

  const isProcessing = formState.isSubmitting || isDeleting;

  const title = getTitle ? getTitle(getValues()) : undefined;

  const getUserEnums = useCallback(async () => {
    if (!controller.userEnums?.length) return;

    try {
      const result = await fetchUserEnums.refetch({ throwOnError: true });
      setUserEnums(result.data || {});
    } catch (err) {
      if (isCancelledError(err)) {
        return;
      }
      console.error("Error fetching user enums:", err);
      throw new Error("Failed to load user enums. Please try again.");
    }
  }, [controller.userEnums, fetchUserEnums]);

  useEffect(() => {
    // Only fetch when dialog state actually changes from closed to open
    // This prevents re-fetching when function references change
    if (!isDialogShown) {
      lastDialogState.current = false;
      return;
    }

    // Skip if dialog was already open (prevents infinite loop from unstable deps)
    if (lastDialogState.current === true) {
      return;
    }
    lastDialogState.current = true;

    const fetchInitialData = async () => {
      try {
        setFetchError(null);
        setLoading(true);
        setGlobalError(null);
        await getUserEnums();
        try {
          const data = await fetchData();
          reset(data);
        } catch (error) {
          console.error("Error fetching data:", error);
          throw new Error("Failed to fetch initial data. Please try again.");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred.";
        setFetchError(errorMessage);
        setGlobalError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
    // Note: fetchData and getUserEnums are intentionally excluded from deps
    // as they are unstable references that would cause infinite re-fetching.
    // The ref pattern ensures we only fetch when the dialog actually opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogShown, reset]);

  const onValid = async (data: T) => {
    try {
      setGlobalError(null);
      const result = await submitFn(data);
      if (result) {
        handleFormCallback();
      } else {
        throw new Error("Unexpected response from submit function");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred during submission.";
      setGlobalError(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!deleteFn) return;
    try {
      setGlobalError(null);
      setIsDeleting(true);
      await deleteFn();
      handleFormCallback();
    } catch (error) {
      console.error("Delete error:", error);
      const errorMessage = getErrorMessage(
        error as Error,
        "An unexpected error occurred during deletion."
      );
      setGlobalError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteDialogOpen = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
  };

  const deleteFunctionality: DeleteDialogFunctionality | undefined = deleteFn
    ? {
        onDelete: handleDelete,
        deleteWarningMessage: deleteWarningMessage,
        onClose: handleDeleteDialogClose,
        openDialog: handleDeleteDialogOpen,
        isDialogOpen: showDeleteDialog,
      }
    : undefined;

  const areAllMetadataVisible =
    Object.entries(visibleMetadata).length > 0 &&
    Object.values(visibleMetadata).every((value) => value);

  const setMetadataFieldVisibility = (field: string, value: boolean) => {
    setVisibleMetadata((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleMetadataFieldVisibility = (field: string) => {
    setVisibleMetadata((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const toggleAllMetadataVisibility = () => {
    const newVisibility = !areAllMetadataVisible;
    setVisibleMetadata((prev) =>
      Object.keys(prev).reduce(
        (acc, key) => ({ ...acc, [key]: newVisibility }),
        {}
      )
    );
  };

  const hasMetadata = metadataFields !== undefined && metadataFields.length > 0;

  const isMetadataFieldVisible = (field: string) =>
    visibleMetadata[field] || false;

  const hasMetadataField = (field: string) =>
    metadataFields?.includes(field) || false;

  return {
    fetchError,
    hasMetadata,
    hasMetadataField,
    toggleMetadataFieldVisibility,
    setMetadataFieldVisibility,
    isMetadataFieldVisible,
    areAllMetadataVisible,
    toggleAllMetadataVisibility,
    userEnums,
    title,
    loading,
    globalError,
    methods,
    isSubmitting: formState.isSubmitting,
    isDeleting,
    isProcessing,
    isCreateMode,
    onSubmit: handleSubmit(onValid),
    onCancel: handleFormCallback,
    deleteFunctionality,
  };
};

// ============================================
// CONTEXT
// ============================================

/**
 * Context for child components to access entity handler state.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const EntityHandlerContext = createContext<EntityHandlerReturn<any> | null>(
  null
);

/**
 * Hook for child components to access entity handler state.
 */
export const useEntityHandlerContext = <
  T extends FieldValues,
>(): EntityHandlerReturn<T> => {
  const context = useContext(EntityHandlerContext);
  if (!context) {
    throw new Error(
      "useEntityHandlerContext must be used within an EntityHandlerContext.Provider"
    );
  }
  return context as EntityHandlerReturn<T>;
};
